import { CarouselContext } from 'pure-react-carousel'
import { FC, useCallback, useContext, useEffect, useState } from 'react'

import { RenderOnceInViewport } from '../../../app/_components/performance/RenderOnceInViewport.client'
import { GalleryAsset } from '../../../components/pages/products/ProductPage/contexts/GalleryImageStateContainer'
import { PaginationDots } from '../../../components/pages/products/ProductPage/GalleryImageCarousel/PaginationDots'
import { PaginationDotsWrapper } from '../../../components/pages/products/ProductPage/GalleryImageCarousel/styles'

import { CarouselSlide, CarouselSlider, SlideVisibilityTarget } from './styles'
import { usePreventScrollOnSwipe } from './usePreventScrollOnSwipe'

interface IProps {
  assets: GalleryAsset[]
  hidePagination?: boolean
  isImageZoomShown?: boolean
  onClick?: React.MouseEventHandler<HTMLDivElement>
  renderAsset: (asset: GalleryAsset, index: number) => JSX.Element | null
  setCurrentImageIndex: (index: number) => void
  scrollableSlides?: boolean
}

/**
 * Account for prepended "virtual slide".
 */
const getSlideIndex = (imageIndex: number) => imageIndex + 1
const getImageIndex = (slideIndex: number) => slideIndex - 1

/**
 * Note: pure-react-carousel does not support infinite loop sliding.
 * Hence this hack is need to mimic the desired behavior (Stas 11/08/21)
 */
export const InfiniteSlider: FC<IProps> = ({
  assets,
  hidePagination = false,
  isImageZoomShown = false,
  onClick,
  renderAsset,
  setCurrentImageIndex,
  scrollableSlides,
}) => {
  const carouselContext = useContext(CarouselContext)
  const [firstImageIndex, lastImageIndex] = [0, assets.length - 1]
  const [firstRealSlideIndex, lastRealSlideIndex] = [
    getSlideIndex(firstImageIndex),
    getSlideIndex(lastImageIndex),
  ]
  const [firstVirtualSlideIndex, lastVirtualSlideIndex] = [
    firstRealSlideIndex - 1,
    lastRealSlideIndex + 1,
  ]

  /**
   * Must subscribe to use CarouselContext.
   * @see https://github.com/express-labs/pure-react-carousel#hooks-and-usecontext
   */
  const [currentSlide, setCurrentSlide] = useState(carouselContext.state.currentSlide)

  useEffect(() => {
    let timeoutId: NodeJS.Timeout
    function onChange() {
      if (timeoutId) {
        clearTimeout(timeoutId)
      }
      timeoutId = setTimeout(() => {
        setCurrentSlide(carouselContext.state.currentSlide)
      }, 0)
    }

    carouselContext.subscribe(onChange)

    return () => {
      carouselContext.unsubscribe(onChange)
      if (timeoutId) {
        clearTimeout(timeoutId)
      }
    }
  }, [carouselContext])

  useEffect(() => {
    const timeoutId = setTimeout(() => {
      setCurrentImageIndex(getImageIndex(currentSlide))
    }, 0)
    return () => clearTimeout(timeoutId)
  }, [currentSlide, setCurrentImageIndex])

  const goToSlideWithoutAnimation = useCallback(
    (newSlideIndex: number) => {
      carouselContext.setStoreState({ currentSlide: newSlideIndex, disableAnimation: true })

      const timeoutId = setTimeout(() => {
        carouselContext.setStoreState({ disableAnimation: false })
      }, 0)

      return () => clearTimeout(timeoutId)
    },
    [carouselContext]
  )

  const updateCurrentSlide = () => {
    if (currentSlide === firstVirtualSlideIndex) {
      goToSlideWithoutAnimation(lastRealSlideIndex)
    } else if (currentSlide === lastVirtualSlideIndex) {
      goToSlideWithoutAnimation(firstRealSlideIndex)
    }
  }

  const sharedSlideProps = {
    'aria-selected': undefined,
    'role': undefined,
    'tabIndex': -1,
  }

  const hasAssets = !!assets.length

  const preventScrollOnSwipe = usePreventScrollOnSwipe()

  return (
    <>
      <CarouselSlider
        onClick={e => onClick && onClick(e)}
        onTouchStart={preventScrollOnSwipe}
        onTransitionEnd={e => e.propertyName === 'transform' && updateCurrentSlide()}
        role={undefined}
        tabIndex={-1}
      >
        {hasAssets && (
          <CarouselSlide
            {...sharedSlideProps}
            aria-hidden
            index={firstVirtualSlideIndex}
            key={firstVirtualSlideIndex}
          >
            <RenderOnceInViewport partialVisibility VisibilityTarget={SlideVisibilityTarget}>
              {renderAsset(assets[lastImageIndex]!, getImageIndex(firstVirtualSlideIndex))}
            </RenderOnceInViewport>
          </CarouselSlide>
        )}
        {assets.map((asset, imageIndex) => (
          <CarouselSlide
            {...sharedSlideProps}
            index={imageIndex}
            key={imageIndex}
            $overflowY={scrollableSlides ? 'scroll' : undefined}
          >
            <RenderOnceInViewport
              forceRender={
                imageIndex === 0 ||
                (currentSlide === firstVirtualSlideIndex && imageIndex === lastImageIndex) ||
                (currentSlide === lastVirtualSlideIndex && imageIndex === firstImageIndex)
              }
              partialVisibility
              VisibilityTarget={SlideVisibilityTarget}
            >
              {renderAsset(asset, imageIndex)}
            </RenderOnceInViewport>
          </CarouselSlide>
        ))}
        {hasAssets && (
          <CarouselSlide
            {...sharedSlideProps}
            aria-hidden
            index={lastVirtualSlideIndex}
            key={lastVirtualSlideIndex}
          >
            <RenderOnceInViewport partialVisibility VisibilityTarget={SlideVisibilityTarget}>
              {renderAsset(assets[firstImageIndex]!, getImageIndex(lastVirtualSlideIndex))}
            </RenderOnceInViewport>
          </CarouselSlide>
        )}
      </CarouselSlider>
      {!hidePagination && (
        <PaginationDotsWrapper {...{ isImageZoomShown }}>
          <PaginationDots currentIndex={getImageIndex(currentSlide)} elementCount={assets.length} />
        </PaginationDotsWrapper>
      )}
    </>
  )
}
